import { ReactElement, useCallback, useMemo, memo } from 'react';
import { CircularProgress } from '@mui/material';
import {
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRoute as RouteIcon,
  faClone as DuplicateIcon,
  faHistory as MarkAsPendingIcon,
  faFileArrowDown as ExportListIcon,
  faCommentDots as ModifyEvidenceIcon,
  faFileInvoiceDollar as BillingIcon,
} from '@fortawesome/free-solid-svg-icons';
import { isNull } from 'lodash';

import { PlanFeaturesTypes, RouteItemStateType } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { ItemExtended } from '~services/item/types';
import { isAdminUser, isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
import { getFeatureAvailableToDate } from '~utils/feature';
import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { isRouteStopOrderFinalized } from '~utils/route';
import { getUrlTrackingOrder, openUrlInNewTab } from '~utils/commons';
import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';
import MenuActionsDataTable from '~components/MenuActionsDataTable';
import { ShowIcon } from '~components/MenuActionsDataTable/styles';

import { useDashboardVariantV2CarrierContentFinalizedTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

export interface DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenuProps {
  data: ItemExtended;
  onDowloadEvidencesOrderPDF: (trackingCode: string) => void;
  loadingDowloadEvidencesOrderPDF: boolean;
}

const DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenu = ({
  data,
  loadingDowloadEvidencesOrderPDF,
  onDowloadEvidencesOrderPDF,
}: DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenuProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isAdmin = useMemo(() => isAdminUser(user), [user]);
  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isCreatorOrderCurrentUser = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      feedback: { handleOpen: handleOpenDialogFeedback },
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
      changeStateOrder: { handleOpen: handleOpenDialogChangeStateOrder },
      modifyAssignmentOrder: {
        handleOpen: handleOpenDialogModifyAssignmentOrder,
      },
      markAsPending: { handleOpen: handleOpenDialogMarkAsPending },
      setFeedbackStop: { handleOpen: handleOpenDialogSetFeedbackStop },
      rebillOrder: { handleOpen: handleOpenDialogRebillOrder },
    },
  } = useDashboardVariantV2CarrierContentFinalizedTabContext();

  const handleRedirectToNewOrder = useCallback(
    (extraData?: Record<string, unknown>) => {
      navigate(PATHS.ORDERS.NEW, { state: extraData });
    },
    [navigate],
  );

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isCreatorOrderCurrentUser && data.canModify,
    [isUserOnlyReader, isCreatorOrderCurrentUser, data.canModify],
  );

  const handleShowOrder = useCallback(() => {
    if (!!data.routeCode && !!data.hasFeedback && !!data.routeItemId) {
      handleOpenDialogFeedback({
        stopId: data.routeItemId as string,
        orderId: data[ITEM_KEY_ID],
      });

      return;
    }

    handleOpenDialogDetailsOrder({
      orderId: data[ITEM_KEY_ID],
      currentTab: null,
    });
  }, [data, handleOpenDialogDetailsOrder, handleOpenDialogFeedback]);

  const enabledChangeOrder = useMemo(
    () => !isUserOnlyReader && !data.routeCode,
    [data.routeCode, isUserOnlyReader],
  );

  const enabledModifyAssignment = useMemo(
    () =>
      !isUserOnlyReader &&
      data.itemStateTypeId !== RouteItemStateType.FinalizedSuccess,
    [data.itemStateTypeId, isUserOnlyReader],
  );

  const isOwnItem = useMemo(
    () => isNull(data.carrierCompanyId),
    [data.carrierCompanyId],
  );

  const isOwnCompany = useMemo(
    () =>
      isCurrentLoggedCompany(data.carrierCompanyId, user?.companyId ?? null),
    [data.carrierCompanyId, user?.companyId],
  );

  const enabledShowRoute = useMemo(() => {
    if (!data.routeCode) return false;

    return !isCreatorOrderCurrentUser && (isOwnItem || isOwnCompany);
  }, [data.routeCode, isCreatorOrderCurrentUser, isOwnCompany, isOwnItem]);

  const pathShowRoute = useMemo(
    () =>
      replaceParamsPath(PATHS.ROUTES.VEHICLE_PLANNING, {
        ':apiTransactionId': data.apiTransactionId as string,
        ':routeId': data.routeId as string,
      }),
    [data.apiTransactionId, data.routeId],
  );

  const enabledDownloadDeliveryTicket = useMemo(() => {
    if (!data.routeCode) return false;

    return isRouteStopOrderFinalized(
      data.itemStateTypeId as RouteItemStateType,
    );
  }, [data.itemStateTypeId, data.routeCode]);

  const enabledModifyEvidence = useMemo(() => {
    if (!data.routeCode) return false;

    return !isUserOnlyReader && !isCreatorOrderCurrentUser;
  }, [data.routeCode, isCreatorOrderCurrentUser, isUserOnlyReader]);

  const enableRebill = useMemo(
    () =>
      isAdmin && data.itemStateTypeId === RouteItemStateType.FinalizedSuccess,
    [data.itemStateTypeId, isAdmin],
  );

  return (
    <MenuActionsDataTable
      resource={{
        path: PATHS.ORDERS.BASE,
        id: data[ITEM_KEY_ID],
      }}
      items={{
        show: {
          enabled: true,
          onClick: handleShowOrder,
          divider: !enabledEdit,
        },
        edit: {
          enabled: enabledEdit,
          divider: true,
        },
      }}
      extraItems={[
        {
          enabled: enabledChangeOrder,
          label: 'Cambiar estado',
          Icon: <ChangeStateIcon color="success" />,
          onClick: () =>
            handleOpenDialogChangeStateOrder({
              itemsIds: [data[ITEM_KEY_ID]],
              excludeStateOption: data.itemStateTypeId,
            }),
          divider: !enabledModifyAssignment,
        },
        {
          enabled: enabledModifyAssignment,
          label: 'Modificar asignación',
          Icon: <ModifyAssignmentIcon color="success" />,
          onClick: () =>
            handleOpenDialogModifyAssignmentOrder(data[ITEM_KEY_ID]),
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        {
          enabled: enabledShowRoute,
          label: 'Ver ruta',
          Icon: <ShowIcon />,
          resource: { path: pathShowRoute },
        },
        {
          enabled: true,
          label: 'Ver seguimiento de órden',
          Icon: <FontAwesomeIcon icon={RouteIcon} />,
          onClick: () => {
            openUrlInNewTab(getUrlTrackingOrder(data.trackingCode));
          },
          divider: enabledEdit || enabledDownloadDeliveryTicket,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.TrackingPortalModule,
          },
        },
        {
          enabled: enabledEdit,
          label: 'Duplicar',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          onClick: () =>
            handleRedirectToNewOrder({ duplicateById: data[ITEM_KEY_ID] }),
        },
        {
          enabled: enabledEdit,
          label: 'Marcar como pendiente',
          Icon: <FontAwesomeIcon icon={MarkAsPendingIcon} />,
          onClick: () => handleOpenDialogMarkAsPending(data),
          divider:
            enabledDownloadDeliveryTicket ||
            enabledModifyEvidence ||
            enableRebill,
        },
        {
          enabled: enabledDownloadDeliveryTicket,
          label: 'Descargar comprobante de visita',
          Icon: loadingDowloadEvidencesOrderPDF ? (
            <CircularProgress color="primary" disableShrink size={20} />
          ) : (
            <FontAwesomeIcon icon={ExportListIcon} />
          ),
          onClick: () => onDowloadEvidencesOrderPDF(data.trackingCode),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.TrackingEPOD,
          },
        },
        {
          enabled: enabledModifyEvidence,
          label: 'Modificar evidencia',
          Icon: <FontAwesomeIcon icon={ModifyEvidenceIcon} />,
          onClick: () => handleOpenDialogSetFeedbackStop(data),
        },
        {
          enabled: enableRebill,
          label: 'Retarifar',
          Icon: <FontAwesomeIcon icon={BillingIcon} />,
          onClick: () => handleOpenDialogRebillOrder(data[ITEM_KEY_ID]),
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmBillingGiver,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmBillingGiver,
            ),
          },
        },
      ]}
    />
  );
};

export default memo(
  DashboardVariantV2CarrierContentFinalizedTabDataTableActionsMenu,
);
